import { useState } from "react";
import axios from "axios";
import { LockKeyhole, ListChecks } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RevealSection } from "@/components/common/RevealSection";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLeadsPage() {
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [leads, setLeads] = useState([]);

  const fetchLeads = async (event) => {
    event.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      const response = await axios.get(`${API}/leads`, {
        headers: {
          "x-admin-password": password,
        },
      });
      setLeads(response.data);
    } catch (requestError) {
      setLeads([]);
      setError("Access denied. Please check your password.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="px-6 md:px-12 lg:px-24" data-testid="admin-leads-page">
      <RevealSection className="mx-auto w-full max-w-7xl py-14 md:py-20">
        <div className="rounded-2xl border border-border bg-white p-8 soft-card">
          <p className="text-base text-primary md:text-lg" data-testid="admin-page-label">
            Teacher inquiry dashboard
          </p>
          <h1 className="mt-2 text-4xl font-bold leading-tight sm:text-5xl" data-testid="admin-page-heading">
            View all website inquiries
          </h1>

          <form onSubmit={fetchLeads} className="mt-6 flex flex-col gap-3 sm:flex-row" data-testid="admin-login-form">
            <Input
              type="password"
              placeholder="Enter dashboard password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              className="h-12 rounded-xl border-border bg-white"
              required
              data-testid="admin-password-input"
            />
            <Button
              type="submit"
              className="h-12 rounded-full bg-primary px-7 text-primary-foreground"
              data-testid="admin-load-inquiries-button"
            >
              <LockKeyhole className="h-4 w-4" />
              {isLoading ? "Loading..." : "Load inquiries"}
            </Button>
          </form>

          {error ? (
            <p className="mt-3 text-sm text-destructive" data-testid="admin-error-message">
              {error}
            </p>
          ) : null}
        </div>

        <div className="mt-6 rounded-2xl border border-border bg-white p-8 soft-card" data-testid="admin-leads-list-section">
          <div className="flex items-center justify-between gap-3">
            <p className="text-lg font-semibold text-primary" data-testid="admin-leads-list-title">
              Saved inquiries
            </p>
            <p className="text-sm text-muted-foreground" data-testid="admin-leads-count">
              {leads.length} total
            </p>
          </div>

          {leads.length === 0 ? (
            <div className="mt-4 flex items-center gap-2 rounded-xl border border-dashed border-border p-4 text-sm text-muted-foreground" data-testid="admin-empty-leads">
              <ListChecks className="h-4 w-4" />
              Enter password and load inquiries to view submissions.
            </div>
          ) : (
            <div className="mt-5 grid gap-4" data-testid="admin-leads-list">
              {leads.map((lead) => (
                <article key={lead.id} className="rounded-xl border border-border bg-muted/30 p-4" data-testid={`admin-lead-card-${lead.id}`}>
                  <p className="text-base font-semibold text-foreground" data-testid={`admin-lead-name-${lead.id}`}>
                    {lead.full_name}
                  </p>
                  <p className="text-sm text-muted-foreground" data-testid={`admin-lead-meta-${lead.id}`}>
                    {lead.email} • {lead.country} • {lead.form_type}
                  </p>
                  <p className="mt-2 text-sm text-foreground/90" data-testid={`admin-lead-goals-${lead.id}`}>
                    Goal: {lead.goals}
                  </p>
                  <p className="mt-1 text-sm text-foreground/90" data-testid={`admin-lead-schedule-${lead.id}`}>
                    Timing: {lead.preferred_schedule}
                  </p>
                  <p className="mt-1 text-sm text-foreground/90" data-testid={`admin-lead-message-${lead.id}`}>
                    Message: {lead.message || "-"}
                  </p>
                </article>
              ))}
            </div>
          )}
        </div>
      </RevealSection>
    </div>
  );
}